import React, { useState, useEffect } from "react";
import { fetchMasterData, fetchBanners } from "../api";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { useNavigate } from "react-router-dom";
import "../assets/css/HeroSection.css";
import { resolveImageUrl } from "../utils/imageUrl";

const HeroSection = () => {
  const navigate = useNavigate();

  const [gotras, setGotras] = useState([]);
  const [cities, setCities] = useState([]);
  const [banners, setBanners] = useState([]);
  const [mIdx, setMIdx] = useState(0);
  /** Mobile hero: API image failed to load — show CSS fallback instead of broken <img>. */
  const [mobileImgFailed, setMobileImgFailed] = useState(false);

  const [selectedGotra, setSelectedGotra] = useState("");
  const [selectedAge, setSelectedAge] = useState("");
  const [selectedCity, setSelectedCity] = useState("");

  const ageRanges = [
    { id: "18-25", name: "18 to 25" },
    { id: "26-30", name: "26 to 30" },
    { id: "31-40", name: "31 to 40" },
    { id: "41-50", name: "41 to 50" },
  ];

  /* LOAD MASTER DATA */
  useEffect(() => {
    fetchMasterData("gotras").then(setGotras);
    fetchMasterData("cities").then(setCities);
  }, []);

  /* AUTO-ROTATE the simple mobile banner */
  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => {
      setMIdx((i) => (i + 1) % banners.length);
    }, 4000);
    return () => clearInterval(t);
  }, [banners.length]);

  useEffect(() => {
    setMobileImgFailed(false);
  }, [banners]);

  /* LOAD BANNERS */
  useEffect(() => {
    const normalizeRow = (b, idx) => {
      if (!b || typeof b !== "object") return null;
      const id = b.BannerID ?? b.banner_id ?? b.id ?? `banner-${idx}`;
      const img = b.BannerImage ?? b.banner_image ?? b.image ?? "";
      const title = b.BannerTitle ?? b.banner_title ?? "Banner";
      const order = Number(b.DisplayOrder ?? b.display_order ?? idx) || 0;
      if (!String(img).trim()) return null;
      return {
        ...b,
        BannerID: id,
        BannerImage: String(img).trim(),
        BannerTitle: title,
        DisplayOrder: order,
      };
    };

    const loadBanner = async () => {
      try {
        let list = await fetchBanners("top");
        if (!Array.isArray(list) || list.length === 0) {
          list = await fetchBanners("bottom");
        }
        const final = list
          .map(normalizeRow)
          .filter(Boolean)
          .sort((a, b) => a.DisplayOrder - b.DisplayOrder)
          .map((b) => ({
            ...b,
            fullImage: resolveImageUrl(b.BannerImage),
          }))
          .filter((b) => b.fullImage);

        setBanners(final);
      } catch (e) {
        console.warn("Hero banners:", e?.message || e);
        setBanners([]);
      }
    };

    loadBanner();
  }, []);

  /* SEARCH */
  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams({
      age: selectedAge,
      city: selectedCity,
      gotra: selectedGotra,
    }).toString();

    navigate(`/all-profiles?${params}`);
  };

  /* ============================================================
     SHARED MARKUP HELPERS
     ============================================================ */
  const renderSearchFields = () => (
    <>
      <select
        value={selectedAge}
        onChange={(e) => setSelectedAge(e.target.value)}
      >
        <option value="">Age</option>
        {ageRanges.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </select>

      <select
        value={selectedGotra}
        onChange={(e) => setSelectedGotra(e.target.value)}
      >
        <option value="">Gotra</option>
        {gotras.map((g) => (
          <option key={g.id} value={g.name}>
            {g.name}
          </option>
        ))}
      </select>

      <select
        value={selectedCity}
        onChange={(e) => setSelectedCity(e.target.value)}
      >
        <option value="">City</option>
        {cities.map((c) => (
          <option key={c.id} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>

      <button type="submit">Search</button>
    </>
  );

  const mobileSlide = banners.length > 0 ? banners[mIdx % banners.length] : null;
  const showMobilePhoto = mobileSlide && !mobileImgFailed;

  return (
    <>
      {/* ============================================================
          DESKTOP / TABLET HERO  (>= 769px)
          Uses the original overlay layout.
          ============================================================ */}
      <section
        className={`hero-container hero-desktop-only${
          banners.length === 0 ? " hero-api-banners-empty" : ""
        }`}
      >
        {banners.length > 0 && (
          <Carousel
            autoPlay
            infiniteLoop
            showThumbs={false}
            showStatus={false}
            interval={4000}
            stopOnHover={false}
            swipeable
            emulateTouch
            className="hero-carousel"
          >
            {banners.map((b) => (
              <div key={b.BannerID} className="hero-slide">
                <div
                  className="hero-blur-bg"
                  style={{ backgroundImage: `url(${b.fullImage})` }}
                ></div>
                <img
                  src={b.fullImage}
                  alt={b.BannerTitle || "Banner"}
                  className="hero-banner-img"
                  referrerPolicy="no-referrer-when-downgrade"
                  onError={(e) => {
                    e.target.style.visibility = "hidden";
                  }}
                />
              </div>
            ))}
          </Carousel>
        )}

        <div className="hero-overlay"></div>

        <div className="hero-content">
          <div className="hero-content-top">
            <h1 className="hero-headline">
              <span className="hero-headline__en">
                Find Your <span className="hero-gold">Perfect Match</span>
              </span>
              <span className="hero-headline__hi">सही जीवनसाथी खोजें</span>
            </h1>

            <p className="subtext">
              Trusted by thousands of families to find the right life partner.
              <br />
              हजारों परिवारों द्वारा विश्वसनीय सेवा।
            </p>
          </div>

          <form className="hero-search" onSubmit={handleSearch}>
            {renderSearchFields()}
          </form>
        </div>
      </section>

      {/* ============================================================
          MOBILE HERO  (<= 768px)
          Two clean stacked sections: banner + headline/form card.
          We use a plain rotating <img> instead of the Carousel
          component because the carousel collapses to height 0 inside
          this layout context.
          ============================================================ */}
      <section className="mhero">
        <div className="mhero-stack">
          <div className="mhero-banner">
            {showMobilePhoto ? (
              <img
                key={mobileSlide.fullImage}
                src={mobileSlide.fullImage}
                alt={mobileSlide.BannerTitle || "Banner"}
                className="mhero-banner-img"
                referrerPolicy="no-referrer-when-downgrade"
                onError={() => setMobileImgFailed(true)}
              />
            ) : (
              <div
                className="mhero-banner-img mhero-banner-fallback"
                role="img"
                aria-label="Welcome to Dewangan Links"
              />
            )}

            <div className="mhero-banner__shade" aria-hidden="true" />

            {banners.length > 1 && (
              <div className="mhero-dots">
                {banners.map((b, i) => (
                  <button
                    key={b.BannerID}
                    type="button"
                    className={`mhero-dot ${i === mIdx ? "active" : ""}`}
                    onClick={() => setMIdx(i)}
                    aria-label={`Show banner ${i + 1}`}
                  />
                ))}
              </div>
            )}

            <div className="mhero-caption">
              <h1 className="mhero-title hero-headline__en">
                Find Your <span className="hero-gold">Perfect Match</span>
              </h1>
              <p className="mhero-title-hi hero-headline__hi">
                सही जीवनसाथी खोजें
              </p>
            </div>
          </div>

          <div className="mhero-panel">
            <p className="mhero-panel__lead">
              Trusted by thousands of families across the Dewangan community.
            </p>

            <form className="mhero-search" onSubmit={handleSearch}>
              <p className="mhero-search__label">
                <i className="fa fa-search" aria-hidden="true" /> Quick search
              </p>
              {renderSearchFields()}
            </form>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
